import { useRef, useState, useEffect } from 'react';
import { cropToSquareAndResize } from '@/utils/image/cropAndResize';
import clsx from 'clsx';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

export default function SquareLogoUploader({
  label = 'Choose Image',
  accept = 'image/*',
  maxSize = 512,
  mimeType = 'image/webp',
  quality = 0.9,
  initialPreview = null,
  disabled = false,
  onProcessed,            // (result) => { blob, dataUrl, width, height, format }
  maxInputSizeMB = 10,    // guard on raw file before processing
  showPreview = true,     // <- NEW: hide internal preview when you already show an Avatar outside
  previewSize = 80,       // <- NEW: square size (px) when showPreview = true
  bordered = true,        // <- NEW: toggle dashed container
  className,              // optional wrapper class
  caption = `Output: ${maxSize}×${maxSize}, ${mimeType.replace('image/','').toUpperCase()}`, // subtle footnote
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(initialPreview);
  const [error, setError] = useState('');

  // Sync internal preview when the parent's initialPreview prop changes.
  // Without this effect the component is "uncontrolled" — useState only
  // reads initialPreview at mount. Bug it caused: a coach editing Team A
  // (which has a logo), then clicking into Team B's edit page (no logo),
  // would see Team A's logo persist in the uploader because EditTeam.jsx
  // doesn't remount the component between team navigations.
  useEffect(() => {
    setPreview(initialPreview);
  }, [initialPreview]);

  const pick = () => inputRef.current?.click();

  async function handleFile(file) {
    setError('');
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > maxInputSizeMB * 1024 * 1024) {
      setError(`Image too large. Max ${maxInputSizeMB}MB.`);
      return;
    }

    setBusy(true);
    try {
      const result = await cropToSquareAndResize(file, { maxSize, mimeType, quality });
      setPreview(result.dataUrl);
      onProcessed?.(result);
    } catch (e) {
      console.error(e);
      setError('Failed to process image.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={clsx('space-y-2', className)}>
      <div
        className={clsx(
          'flex items-center gap-4 rounded-2xl p-3 sm:p-4',
          bordered ? 'border border-dashed border-zinc-300' : ''
        )}
      >
        {showPreview && (
          <div
            className="rounded-xl overflow-hidden bg-zinc-50 ring-1 ring-zinc-200 flex-shrink-0"
            style={{ width: previewSize, height: previewSize }}
          >
            {preview ? (
              <img
                src={preview}
                alt="Logo preview"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full grid place-items-center text-xs text-zinc-400">
                No Logo
              </div>
            )}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={pick}
              disabled={disabled || busy}
              className={clsx(
                'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-bold uppercase tracking-wide text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all',
                'bg-[#b5f03d] hover:bg-[#94cc28]',
                'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#b5f03d]',
              )}
              style={{ fontFamily: HEAD_FONT }}
            >
              {busy ? 'Processing…' : label}
            </button>
            <span className="text-xs text-zinc-500">auto square + compressed</span>
          </div>

          {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          disabled={disabled || busy}
          onClick={(e) => { e.target.value = null; }} // allow re-select same file
        />
      </div>

      {caption && (
        <p className="text-[11px] leading-5 text-zinc-500">{caption}</p>
      )}
    </div>
  );
}
